import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api-client'
import type { User } from '@/types'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  setUser: (user: User | null) => void
  logout: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isAuthenticated: false,
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      logout: () => {
        apiClient.clearTokens()
        set({ user: null, isAuthenticated: false })
      },
    }),
    {
      name: 'auth-storage',
    }
  )
)

export function useAuthHydrated() {
  const [hydrated, setHydrated] = useState(useAuthStore.persist.hasHydrated())

  useEffect(() => {
    const unsubscribe = useAuthStore.persist.onFinishHydration(() => {
      setHydrated(true)
    })
    return unsubscribe
  }, [])

  return hydrated
}

export function useCurrentUser() {
  const { setUser } = useAuthStore()

  return useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const response = await apiClient.getMe()
      const user = response.data.data as User
      setUser(user)
      return user
    },
    retry: false,
    staleTime: 1000 * 60 * 10, // 10 minutes
  })
}

export function useLogin() {
  const queryClient = useQueryClient()
  const { setUser } = useAuthStore()

  return useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      return apiClient.login(email, password)
    },
    onSuccess: async (data) => {
      if (data.data && !data.data.mfaRequired) {
        const userResponse = await apiClient.getMe()
        setUser(userResponse.data.data as User)
        queryClient.invalidateQueries({ queryKey: ['currentUser'] })
      }
    },
  })
}

export function useVerifyMfa() {
  const queryClient = useQueryClient()
  const { setUser } = useAuthStore()

  return useMutation({
    mutationFn: async ({ mfaToken, code }: { mfaToken: string; code: string }) => {
      return apiClient.verifyMfa(mfaToken, code)
    },
    onSuccess: async () => {
      const userResponse = await apiClient.getMe()
      setUser(userResponse.data.data as User)
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })
}

export function useRegister() {
  const queryClient = useQueryClient()
  const { setUser } = useAuthStore()

  return useMutation({
    mutationFn: async (data: { email: string; password: string; name: string; tenantName: string }) => {
      return apiClient.register(data)
    },
    onSuccess: async () => {
      const userResponse = await apiClient.getMe()
      setUser(userResponse.data.data as User)
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  const { logout } = useAuthStore()

  return useMutation({
    mutationFn: async () => {
      await apiClient.logout()
    },
    onSuccess: () => {
      logout()
      queryClient.clear()
    },
  })
}

export function useSetupMfa() {
  return useMutation({
    mutationFn: async () => {
      const response = await apiClient.setupMfa()
      return response.data.data
    },
  })
}

export function useEnableMfa() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (code: string) => {
      return apiClient.enableMfa(code)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })
}

export function useDisableMfa() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (code: string) => {
      return apiClient.disableMfa(code)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currentUser'] })
    },
  })
}
